import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Briefcase, GraduationCap, Sparkles, Wallet, Users, ArrowRight, Check, Globe2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Logo } from "@/components/Logo";
import { ThemeToggle, LanguageToggle } from "@/components/ThemeLangControls";
import { useI18n } from "@/context/I18nContext";
import { useEffect, useState } from "react";
import api from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1522071820081-009f0129c71c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxnbG9iYWwlMjB0ZWFtJTIwd29ya2luZyUyMG9mZmljZXxlbnwwfHx8fDE3OTA3NDQwOTh8MA&ixlib=rb-4.1.0&q=85";

const UNIVERSES = [
  { key: "work", icon: Briefcase, color: "bg-[#C86A51]" },
  { key: "learn", icon: GraduationCap, color: "bg-[#4C86B8]" },
  { key: "create", icon: Sparkles, color: "bg-[#8E6BB8]" },
  { key: "money", icon: Wallet, color: "bg-[#3E9B87]" },
  { key: "community", icon: Users, color: "bg-[#E6A840]" },
];

export default function Landing() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);

  useEffect(() => { api.get("/plans").then(({ data }) => setPlans(data.filter(p => p.code !== "BUSINESS"))).catch(() => {}); }, []);

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 h-16 bg-background/70 backdrop-blur-xl border-b">
        <div className="max-w-7xl mx-auto h-full px-5 lg:px-8 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-1 sm:gap-2">
            <LanguageToggle />
            <ThemeToggle />
            <Button variant="ghost" onClick={() => navigate("/login")} data-testid="landing-login-btn" className="rounded-full">{t("landing.login")}</Button>
            <Button onClick={() => navigate("/register")} data-testid="landing-signup-btn" className="rounded-full">{t("landing.getStarted")}</Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-14 lg:pt-24 pb-16 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <Badge variant="secondary" className="rounded-full mb-5 gap-1.5 py-1.5 px-3">
            <Globe2 className="h-3.5 w-3.5" /> {t("landing.badge")}
          </Badge>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
            {t("landing.heroTitle")}
          </h1>
          <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
            {t("landing.heroSub")}
          </p>
          <p className="mt-4 font-display font-semibold text-primary tracking-wide">{t("landing.universes")}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button size="lg" onClick={() => navigate("/register")} data-testid="hero-getstarted-btn" className="rounded-full h-12 px-7 text-base">
              {t("landing.getStarted")} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button size="lg" variant="outline" onClick={() => document.getElementById("how")?.scrollIntoView({ behavior: "smooth" })} className="rounded-full h-12 px-7 text-base" data-testid="hero-how-btn">
              {t("landing.seeHow")}
            </Button>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.1 }}
          className="relative">
          <div className="absolute -inset-4 bg-primary/10 rounded-[2rem] blur-2xl" />
          <img src={HERO} alt="URLIFA" className="relative rounded-[2rem] w-full h-[380px] lg:h-[460px] object-cover border shadow-xl" />
        </motion.div>
      </section>

      {/* Universes */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-16">
        <div className="max-w-2xl">
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">{t("landing.valueTitle")}</h2>
          <p className="mt-3 text-muted-foreground">{t("landing.valueSub")}</p>
        </div>
        <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {UNIVERSES.map((u, i) => {
            const Icon = u.icon;
            return (
              <motion.div key={u.key} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`rounded-2xl border bg-card p-6 hover:-translate-y-1 transition-transform ${i === 0 ? "lg:col-span-1" : ""}`} data-testid={`universe-${u.key}`}>
                <div className={`h-12 w-12 rounded-xl ${u.color} flex items-center justify-center text-white mb-4`}>
                  <Icon className="h-6 w-6" />
                </div>
                <h3 className="font-display text-xl font-semibold">{t(`nav.${u.key}`)}</h3>
                <p className="text-sm text-primary font-medium mt-0.5">{t(`universes.${u.key}`)}</p>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{t(`universes.${u.key}Desc`)}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="max-w-7xl mx-auto px-5 lg:px-8 py-16">
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-center">{t("landing.howTitle")}</h2>
        <div className="mt-12 grid gap-8 md:grid-cols-3">
          {[1, 2, 3].map((n) => (
            <div key={n} className="text-center" data-testid={`how-step-${n}`}>
              <div className="mx-auto h-14 w-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center font-display font-bold text-xl">{n}</div>
              <h3 className="mt-4 font-display text-lg font-semibold">{t(`landing.how${n}`)}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{t(`landing.how${n}d`)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-16">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">{t("landing.pricingTitle")}</h2>
          <p className="mt-3 text-muted-foreground">{t("landing.pricingSub")}</p>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-3 max-w-5xl mx-auto">
          {plans.map((p, i) => (
            <div key={p.code} className={`rounded-2xl border p-6 bg-card ${i === 1 ? "ring-2 ring-primary" : ""}`} data-testid={`plan-${p.code}`}>
              <h3 className="font-display text-xl font-semibold">{p.name}</h3>
              <div className="mt-3">
                <span className="font-display text-3xl font-bold">{p.price_monthly === 0 ? "0" : p.price_monthly}</span>
                <span className="text-muted-foreground text-sm"> {p.currency} {t("pricing.month")}</span>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">{p.monthly_credits} {t("pricing.perMonth")}</p>
              <Button className="mt-5 w-full rounded-full" variant={i === 1 ? "default" : "outline"} onClick={() => navigate("/register")} data-testid={`plan-cta-${p.code}`}>
                {t("landing.getStarted")}
              </Button>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-16">
        <div className="rounded-3xl bg-foreground text-background p-10 lg:p-16 text-center relative overflow-hidden grain">
          <ShieldCheck className="mx-auto h-10 w-10 mb-4 opacity-80" />
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight max-w-2xl mx-auto">{t("landing.ctaTitle")}</h2>
          <p className="mt-3 opacity-80 max-w-xl mx-auto">{t("landing.ctaSub")}</p>
          <Button size="lg" onClick={() => navigate("/register")} data-testid="cta-getstarted-btn" className="mt-7 rounded-full h-12 px-8 bg-primary text-primary-foreground hover:bg-primary/90">
            {t("landing.getStarted")} <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>

      <footer className="border-t">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Logo />
          <p className="text-sm text-muted-foreground">© 2026 URLIFA — {t("landing.footer")}</p>
        </div>
      </footer>
    </div>
  );
}
